import { FORBIDDEN_RESPONSE_STATUS } from "../common/constants";
import Shared from "../common/shared";

type RequestMethod = "GET" | "POST" | "DELETE";

export interface ApiResponse<T> {
	responseObj?: T;
	error?: string;
}

export default async function apiPlugin<T>(
	method: RequestMethod,
	route: string,
	urlParams?: URLSearchParams,
	requestData?: object | any[],
	skipReadingResponseBody?: boolean,
	loadFromPublicFolder?: boolean
): Promise<ApiResponse<T>> {
	try {
		let headers: Record<string, string> = {};
		if (!loadFromPublicFolder) {
			const token = await Shared.getAccessToken();
			if (!token) {
				return {
					error: "Invalid Request",
				};
			}
			// set authorization header
			headers["Authorization"] = "bearer " + token;
		}

		// For Okta requests, set UserGroups header
		if (!loadFromPublicFolder && process.env.REACT_APP_CLIENT_NAME !== "IFRC") {
			const userGroups = Shared.getUserInfoGroups();
			if (!userGroups || !userGroups.length) {
				console.error("Okta User Info - Missing 'groups' claim");
				return {
					error: "Okta User Info - Missing 'groups' claim",
				};
			}
			headers["UserGroups"] = userGroups.join(",");
		}

		let body: string | undefined;
		const url: string = loadFromPublicFolder
			? route
			: `${process.env.REACT_APP_API_BASE_URL}/api/${route}${
					urlParams ? "?" + urlParams.toString() : ""
			  }`;
		if (requestData) {
			headers["Content-Type"] = "application/json";
			body = JSON.stringify(requestData);
		}

		const res = await fetch(url, {
			method,
			headers,
			body,
		});

		if (res.ok) {
			try {
				const resData = skipReadingResponseBody
					? undefined
					: res.status === 204
					? (([] as unknown) as T) // return [] to avoid parsing error if res status is 204
					: ((await res.json()) as T); //NOTE: not runtime safe
				return {
					responseObj: resData,
				};
			} catch (err) {
				console.error(err);
				return {
					error: "failed to parse response returned by server",
				};
			}
		} else {
			// if response status is 403, return status and redirect to access denied page
			if (res.status === FORBIDDEN_RESPONSE_STATUS) {
				return {
					error: `${res.status}`,
				};
			}
			return res.json().then((text: any) => {
				return {
					error: text.Message
						? text.Message
						: `${res.status} - ${res.statusText || ""}`,
				};
			});
		}
	} catch (err: any) {
		let errorMessage = "failed to call api";
		errorMessage = `${errorMessage} - ${err.message}`;
		return {
			error: errorMessage,
		};
	}
}
